import { Settings as SettingsIcon } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/cn'
import { channelColor } from '@/lib/channelColor'
import { Tooltip } from '@/components/ui/Tooltip'
import type { Channel } from '@/api/endpoints/channels'
import styles from './Sidebar.module.scss'

export interface ManageChannelsLinkProps {
  /** True when the sidebar is collapsed (icon-only mode). */
  collapsed: boolean
  /** Search payload reused on the link so channel context survives the click. */
  navSearch: Record<string, unknown>
  /** Drawer-only: closes the drawer after the click. */
  onNavigate?: (() => void) | undefined
}

/**
 * O caminho até /channels quando o cabeçalho do grupo, que leva a engrenagem,
 * não aparece. Com o sidebar recolhido, vira um ícone com tooltip na coluna
 * dos canais. Numa conta ainda sem canal, vira um cabeçalho "Canais" com a
 * engrenagem, para o dono conseguir conectar o primeiro.
 */
export function ManageChannelsLink({ collapsed, navSearch, onNavigate }: ManageChannelsLinkProps) {
  if (collapsed) {
    return (
      <div className={styles.channels}>
        <Tooltip content="Gerenciar canais" side="right">
          <Link
            to="/channels"
            search={navSearch}
            className={styles.channel}
            aria-label="Gerenciar canais"
            onClick={onNavigate}
          >
            <SettingsIcon aria-hidden className={styles.channelManageIcon} />
          </Link>
        </Tooltip>
      </div>
    )
  }
  return (
    <div className={styles.sectionHeader}>
      <span className={styles.sectionLabel}>Canais</span>
      <Link
        to="/channels"
        search={navSearch}
        className={styles.sectionAction}
        aria-label="Gerenciar canais"
        title="Gerenciar canais"
        onClick={onNavigate}
      >
        <SettingsIcon aria-hidden />
      </Link>
    </div>
  )
}

export interface ChannelGroupProps {
  /** Section header label, e.g. "YouTube" or "TikTok". */
  label: string
  /** Channels to render in this group (already filtered by platform). */
  channels: Channel[]
  /** Currently active channel slug — drives the highlighted row. */
  activeSlug: string | null
  /** True when the sidebar is collapsed (icon-only mode). */
  collapsed: boolean
  /** Search payload reused on the Settings link so channel context survives the click. */
  navSearch: Record<string, unknown>
  /** Click handler for a channel row. */
  onSelect: (slug: string) => void
}

/**
 * Single platform's channel block in the sidebar (header + list of rows).
 * Rendered N times by ``Sidebar`` — once per platform — so YouTube and
 * TikTok can have separate labelled groups while sharing the row markup.
 *
 * Renders nothing when ``channels`` is empty so an account that hasn't
 * connected (e.g.) TikTok yet doesn't see a phantom empty section.
 */
export function ChannelGroup({
  label,
  channels,
  activeSlug,
  collapsed,
  navSearch,
  onSelect,
}: ChannelGroupProps) {
  if (channels.length === 0) return null

  return (
    <>
      {!collapsed ? (
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>{label}</span>
          <Link
            to="/channels"
            search={navSearch}
            className={styles.sectionAction}
            aria-label={`Gerenciar canais ${label}`}
            title={`Gerenciar canais ${label}`}
          >
            <SettingsIcon aria-hidden />
          </Link>
        </div>
      ) : null}
      <div className={styles.channels}>
        {channels.map((ch) => {
          const color = channelColor(ch.slug)
          const isActive = activeSlug === ch.slug
          const button = (
            <button
              key={ch.slug}
              type="button"
              className={cn(styles.channel, isActive && styles.channelActive)}
              aria-current={isActive ? 'true' : undefined}
              aria-label={collapsed ? ch.name : undefined}
              onClick={() => {
                onSelect(ch.slug)
              }}
            >
              <span className={styles.channelDot} style={{ background: color }} aria-hidden />
              <span className={styles.channelName}>{ch.name}</span>
            </button>
          )
          if (collapsed) {
            return (
              <Tooltip key={ch.slug} content={ch.name} side="right">
                {button}
              </Tooltip>
            )
          }
          return button
        })}
      </div>
    </>
  )
}
